import { useState, useEffect } from 'react';
import { getAllFunds, getInstrumentTypesByFund, saveInstrumentType } from '../../../services/firestoreService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import ToggleField from '../../shared/Forms/ToggleField';
import Button from '../../shared/UI/Button';
import PercentageField from '../../shared/Forms/PercentageField';

const EMPTY_RM = {
  // Interest
  has_interest_rate: false,
  interest_rate_type: null,
  interest_payment_frequency: null,
  interest_calculation_basis: null,
  interest_moratorium: false,
  // Principal
  principal_repayment_structure: null,
  amortisation_frequency: null,
  balloon_percentage: null,
  // Moratorium
  has_principal_moratorium: false,
  principal_moratorium_value: null,
  principal_moratorium_unit: 'months',
  // Equity
  has_equity_upside: false,
  conversion_trigger: null,
  // MOIC
  moic_calculation_method: '',
  // Legacy fields (kept for backwards compat)
  has_repayment_schedule: false,
  repayment_type: null,
};

const EMPTY = {
  fund_id: '', name: '', category: '', description: '', is_active: true,
  return_mechanics: { ...EMPTY_RM },
};

const FREQ_OPTIONS = [
  { value: 'monthly',    label: 'Monthly' },
  { value: 'quarterly',  label: 'Quarterly' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual',     label: 'Annual' },
];

export default function InstrumentForm({ instrument, onSaved, onCancel }) {
  const { user } = useAuth();
  const [form, setForm] = useState(instrument
    ? { ...instrument, return_mechanics: { ...EMPTY_RM, ...instrument.return_mechanics } }
    : { ...EMPTY, return_mechanics: { ...EMPTY_RM } }
  );
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [funds, setFunds] = useState([]);
  const [existingInstruments, setExistingInstruments] = useState([]);

  useEffect(() => {
    getAllFunds().then(list => setFunds(list.filter(f => f.is_active !== false)));
  }, []);

  // Load instruments for this fund when fund changes
  useEffect(() => {
    if (!form.fund_id) { setExistingInstruments([]); return; }
    getInstrumentTypesByFund(form.fund_id).then(setExistingInstruments);
  }, [form.fund_id]);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  }

  function setRM(field, value) {
    setForm(prev => ({
      ...prev,
      return_mechanics: { ...prev.return_mechanics, [field]: value },
    }));
  }

  const isDebtOrHybrid = form.category === 'self_liquidating' || form.category === 'hybrid';
  const rm = form.return_mechanics || {};
  const showAmortFields = rm.principal_repayment_structure && rm.principal_repayment_structure !== 'full_bullet';
  const showBalloon = rm.principal_repayment_structure === 'partially_amortising_with_balloon';

  function validate() {
    const e = {};
    if (!form.fund_id) e.fund_id = 'Fund is required';
    if (!form.name?.trim()) e.name = 'Instrument name is required';
    if (!form.category) e.category = 'Category is required';
    if (!form.description?.trim()) e.description = 'Description is required';

    // Duplicate name check (case-insensitive) — only for new instruments or name changes
    if (form.name?.trim() && form.fund_id) {
      const normalized = form.name.trim().toLowerCase();
      const duplicate = existingInstruments.find(i =>
        i.name?.toLowerCase() === normalized && i.id !== instrument?.id
      );
      if (duplicate) {
        e.name = 'An instrument type with this name already exists for this fund.';
      }
    }
    return e;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      const data = { ...form };
      // Keep legacy fields in sync for backwards compat
      if (rm.principal_repayment_structure) {
        data.return_mechanics.has_repayment_schedule = true;
        data.return_mechanics.repayment_type =
          rm.principal_repayment_structure === 'full_bullet' ? 'bullet' : 'amortising';
      }
      await saveInstrumentType({
        instrumentId: instrument?.id || null,
        instrumentData: data,
        performedBy: user.uid,
      });
      setSuccessMsg('Instrument type saved!');
      setTimeout(() => { setSuccessMsg(''); onSaved(); }, 2000);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSaving(false);
    }
  }

  const fundOptions = funds.map(f => ({ value: f.id, label: f.name }));

  return (
    <form onSubmit={handleSubmit}>
      {errors._global && <div className="alert alert-error">{errors._global}</div>}
      {successMsg && <div className="alert alert-success">{successMsg}</div>}

      {/* ── BASIC DETAILS ── */}
      <div className="card mb-4">
        <div className="card-header"><h2>Basic Details</h2></div>
        <div className="card-body">
          <div className="form-grid">
            <SelectField label="Fund" required error={errors.fund_id}
              value={form.fund_id} onChange={e => set('fund_id', e.target.value)}
              options={fundOptions} />
            <FormField label="Instrument Name" required error={errors.name}>
              <input className={`form-input ${errors.name ? 'error' : ''}`}
                value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g. Senior Secured Debt" />
            </FormField>
            <SelectField label="Category" required error={errors.category}
              value={form.category} onChange={e => set('category', e.target.value)}
              options={[
                { value: 'equity', label: 'Equity' },
                { value: 'self_liquidating', label: 'Self-Liquidating (Debt / Revenue Share)' },
                { value: 'hybrid', label: 'Hybrid (Convertible / Mixed)' },
              ]} />
            <ToggleField label="Is Active" checked={form.is_active !== false} onChange={v => set('is_active', v)} />
            <FormField label="Description" required error={errors.description} className="form-full">
              <textarea className={`form-textarea ${errors.description ? 'error' : ''}`}
                value={form.description} onChange={e => set('description', e.target.value)} rows={3} />
            </FormField>
          </div>
        </div>
      </div>

      {/* ── DEBT REPAYMENT STRUCTURE (for self-liquidating and hybrid) ── */}
      {isDebtOrHybrid && (
        <>
          {/* Interest */}
          <div className="card mb-4">
            <div className="card-header"><h2>Interest Structure</h2></div>
            <div className="card-body">
              <div className="form-grid">
                <SelectField label="Interest Rate Type"
                  value={rm.interest_rate_type || ''}
                  onChange={e => setRM('interest_rate_type', e.target.value || null)}
                  options={[
                    { value: 'fixed', label: 'Fixed' },
                    { value: 'floating', label: 'Floating' },
                    { value: 'revenue_linked', label: 'Revenue Linked' },
                  ]} />
                <SelectField label="Interest Payment Frequency"
                  value={rm.interest_payment_frequency || ''}
                  onChange={e => setRM('interest_payment_frequency', e.target.value || null)}
                  options={FREQ_OPTIONS} />
                <SelectField label="Interest Calculation Basis"
                  value={rm.interest_calculation_basis || ''}
                  onChange={e => setRM('interest_calculation_basis', e.target.value || null)}
                  options={[
                    { value: 'on_original_principal', label: 'On Original Principal (flat amount each period)' },
                    { value: 'on_reducing_balance', label: 'On Reducing Balance (decreases as principal repaid)' },
                  ]} />
                <ToggleField
                  label="Has Interest Moratorium"
                  checked={!!rm.interest_moratorium}
                  onChange={v => setRM('interest_moratorium', v)}
                  hint="Grace period during which interest payments are waived or capitalised."
                />
                {rm.interest_moratorium && (
                  <>
                    <FormField
                      label="Interest Moratorium Period"
                      hint="Number of months during which interest payments are waived or capitalised.">
                      <input
                        type="number"
                        min="1"
                        className="form-input"
                        value={rm.interest_moratorium_months || ''}
                        onChange={e => setRM('interest_moratorium_months', parseInt(e.target.value) || null)}
                        placeholder="6"
                      />
                    </FormField>
                    <ToggleField
                      label="Capitalise Interest During Moratorium"
                      checked={!!rm.capitalise_interest_during_moratorium}
                      onChange={v => setRM('capitalise_interest_during_moratorium', v)}
                      hint="On = accrued interest is added to principal balance. Off = interest is waived entirely."
                    />
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Principal */}
          <div className="card mb-4">
            <div className="card-header"><h2>Principal Repayment</h2></div>
            <div className="card-body">
              <div className="form-grid">
                <SelectField label="Principal Repayment Structure"
                  value={rm.principal_repayment_structure || ''}
                  onChange={e => setRM('principal_repayment_structure', e.target.value || null)}
                  options={[
                    { value: 'full_bullet', label: 'Full Bullet (no principal payments until maturity)' },
                    { value: 'fully_amortising', label: 'Fully Amortising (equal instalments, zero balance at maturity)' },
                    { value: 'partially_amortising_with_balloon', label: 'Partially Amortising with Balloon' },
                  ]} />

                {showAmortFields && (
                  <SelectField label="Amortisation Frequency"
                    value={rm.amortisation_frequency || ''}
                    onChange={e => setRM('amortisation_frequency', e.target.value || null)}
                    options={FREQ_OPTIONS} />
                )}

                {showBalloon && (
                  <PercentageField
                    label="Balloon Percentage"
                    name="balloon_percentage"
                    value={rm.balloon_percentage}
                    onChange={e => setRM('balloon_percentage', e.target.value)}
                    hint="% of original principal due as balloon at maturity. e.g. 30 = 30% balloon, 70% amortised."
                  />
                )}
              </div>
            </div>
          </div>

          {/* Principal Moratorium */}
          <div className="card mb-4">
            <div className="card-header"><h2>Moratorium</h2></div>
            <div className="card-body">
              <div className="form-grid">
                <ToggleField
                  label="Has Principal Moratorium"
                  checked={!!rm.has_principal_moratorium}
                  onChange={v => setRM('has_principal_moratorium', v)}
                  hint="Grace period before principal repayments begin. Interest still applies unless interest moratorium is also set."
                />
                {rm.has_principal_moratorium && (
                  <FormField label="Principal Moratorium Period"
                    hint="Principal repayments begin after this period.">
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="number"
                        min="1"
                        className="form-input"
                        style={{ width: 90 }}
                        value={rm.principal_moratorium_value || ''}
                        onChange={e => setRM('principal_moratorium_value', parseInt(e.target.value) || null)}
                        placeholder="6"
                      />
                      <SelectField
                        value={rm.principal_moratorium_unit || 'months'}
                        onChange={e => setRM('principal_moratorium_unit', e.target.value)}
                        options={[
                          { value: 'months', label: 'Months' },
                          { value: 'quarters', label: 'Quarters' },
                        ]}
                      />
                    </div>
                  </FormField>
                )}
              </div>
              {rm.has_principal_moratorium && rm.interest_moratorium && (
                <div className="alert alert-info mt-3" style={{ fontSize: 13 }}>
                  Both principal and interest moratoriums are active. During the moratorium period, no payments are made and accrued interest will be capitalised (added to the outstanding principal balance).
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── EQUITY FIELDS ── */}
      {(form.category === 'equity' || form.category === 'hybrid') && (
        <div className="card mb-4">
          <div className="card-header"><h2>Equity Terms</h2></div>
          <div className="card-body">
            {form.category === 'equity' ? (
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                Equity instruments inherently include equity upside. No configuration needed.
              </div>
            ) : (
              <div className="form-grid">
                {/* Only show toggle for hybrid — not pure equity */}
                <ToggleField label="Has Equity Upside" checked={!!rm.has_equity_upside}
                  onChange={v => setRM('has_equity_upside', v)}
                  hint="Enable for instruments with a conversion or equity kicker component." />
                {rm.has_equity_upside && (
                  <FormField label="Conversion Trigger" className="form-full">
                    <textarea className="form-textarea" rows={2}
                      value={rm.conversion_trigger || ''}
                      onChange={e => setRM('conversion_trigger', e.target.value)}
                      placeholder="Describe the conditions that trigger conversion…" />
                  </FormField>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MOIC CALCULATION ── */}
      <div className="card mb-4">
        <div className="card-header"><h2>MOIC Calculation</h2></div>
        <div className="card-body">
          <FormField label="MOIC Calculation Method"
            hint="Describe how MOIC is calculated for this instrument type — used by the portfolio impact model.">
            <textarea className="form-textarea" rows={2}
              value={rm.moic_calculation_method || ''}
              onChange={e => setRM('moic_calculation_method', e.target.value)}
              placeholder="e.g. Total principal repayments + total interest received / initial investment" />
          </FormField>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="secondary" type="button" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" type="submit" loading={saving}>
          {instrument ? 'Save Changes' : 'Create Instrument Type'}
        </Button>
      </div>
    </form>
  );
}
